import { auth } from '../firebase';
import { buildApiUrl } from '../config/env';

export const MEMORY_CONSENT_VERSION = 'memory-v2';

const getAuthHeaders = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User must be authenticated to manage memories');
  }

  const authToken = await currentUser.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authToken}`,
  };
};

const parseError = async (response, fallback) => {
  try {
    const body = await response.json();
    return body?.error || fallback;
  } catch (error) {
    return fallback;
  }
};

export const listMemories = async ({ type = 'saved' } = {}) => {
  const memories = [];
  const seen = new Set();
  let cursor = null;
  do {
    const response = await fetch(buildApiUrl(`/memories?limit=100&type=${encodeURIComponent(type)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`), {
      method: 'GET', headers: await getAuthHeaders(),
    });
    if (!response.ok) throw new Error(await parseError(response, 'Failed to load memories'));
    const page = await response.json();
    memories.push(...(page.memories || []));
    cursor = page.nextCursor || null;
    if (cursor && seen.has(cursor)) throw new Error('Unable to load the next memory page');
    if (cursor) seen.add(cursor);
  } while (cursor);
  return { memories };
};

export const getMemorySettings = async () => {
  const response = await fetch(buildApiUrl('/memories/settings'), {
    method: 'GET',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to load memory settings'));
  }

  return response.json();
};

export const updateMemorySettings = async ({
  enabled,
  savedMemoriesEnabled,
  chatHistoryEnabled,
  memoryConsentVersion,
}) => {
  const response = await fetch(buildApiUrl('/memories/settings'), {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({
      enabled,
      savedMemoriesEnabled,
      chatHistoryEnabled,
      memoryConsentVersion: memoryConsentVersion || MEMORY_CONSENT_VERSION,
    }),
  });

  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to update memory settings'));
  }

  return response.json();
};

export const backfillUserMemories = async ({
  cursorChatId,
  dryRun = false,
  forceRestart = false,
  maxChats = 25,
  maxMessagesPerChat = 100,
} = {}) => {
  const response = await fetch(buildApiUrl('/memories/backfill'), {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({
      cursorChatId,
      dryRun,
      forceRestart,
      maxChats,
      maxMessagesPerChat,
    }),
  });

  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to backfill memories'));
  }

  return response.json();
};

// Requests a backend retry; never performs indexing in the browser.
export const startMemoryBackfillInBackground = ({ onProgress, onError } = {}) => {
  localStorage.removeItem('arrowai-memory-backfill-running');
  return backfillUserMemories().then(summary => onProgress?.({ summary })).catch(error => onError?.(error));
};

export const createMemory = async (text) => {
  const response = await fetch(buildApiUrl('/memories'), {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ text }),
  });

  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to save memory'));
  }

  return response.json();
};

export const saveProfileMemories = async ({ nickname, occupation }) => {
  const response = await fetch(buildApiUrl('/memories/profile'), {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ nickname, occupation }),
  });

  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to save profile memories'));
  }

  return response.json();
};

export const importExternalMemories = async ({ source, conversations, maxMessages = 250 }) => {
  const response = await fetch(buildApiUrl('/memories/import'), {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({
      source,
      conversations,
      maxMessages,
    }),
  });

  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to import memories'));
  }

  return response.json();
};

export const deleteMemory = async (memoryId) => {
  const response = await fetch(buildApiUrl(`/memories/${memoryId}`), {
    method: 'DELETE',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to delete memory'));
  }

  return response.json();
};

export const deleteChatMemories = async (chatId) => {
  const response = await fetch(buildApiUrl(`/memories/by-chat/${chatId}`), {
    method: 'DELETE',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(await parseError(response, 'Failed to delete chat memories'));
  }

  return response.json();
};
